import { useCallback, useEffect, useRef, useState } from 'react';
import { BURST, BracketRefused, captureBracket, modesFor } from '../../utils/camera/capture';
import { grabBurst, grabFrame } from '../../utils/fieldFrame';

/**
 * The capture mode a camera's shutter uses (utils/camera/capture.js): Single,
 * Burst (3 or 5 frames, the sharpest kept) or an exposure Bracket, only those
 * this camera can do (`modes`). For as long as the camera is open; nothing is
 * stored. `controls` is useCameraControls()'s answer.
 *
 * run(video, options) takes what the mode asks for, with grabFrame's
 * `options` (size, quality, the digital brightness) -> {mode, shots: [{blob,
 * ev?, index?}]}. A single frame is grabbed at once, before anything is
 * awaited, so two quick presses are two moments. A bracket the camera will
 * not take is one frame instead, and says so (`refused`); the exposure is
 * always put back. `busy` while a burst or a bracket is being taken; a press
 * meanwhile is refused (Error 'capture-busy'), never a second run.
 *
 * `zstack: true` offers the Z-stack too (the host runs it: useZStack); run()
 * is not for it.
 */
export function useCaptureMode(controls, { zstack = false } = {}) {
  const [mode, setMode] = useState('single');
  const [frames, setFrames] = useState(BURST.frames[0]);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState('');
  const busyRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const modes = modesFor(controls.caps, { zstack });
  const current = modes.includes(mode) ? mode : 'single';
  const { applyBracket, restoreExposure } = controls;

  const choose = useCallback((next) => {
    setRefused('');
    setMode(next);
  }, []);

  const run = useCallback(async (video, options = {}) => {
    if (busyRef.current) throw new Error('capture-busy');
    if (current === 'single') return { mode: 'single', shots: [{ blob: await grabFrame(video, options) }] };
    busyRef.current = true;
    setBusy(true);
    setRefused('');
    try {
      if (current === 'burst') {
        const kept = await grabBurst(video, { ...options, frames });
        return { mode: 'burst', shots: [{ blob: kept.blob, index: kept.index, scores: kept.scores }] };
      }
      try {
        const shots = await captureBracket({
          apply: applyBracket, restore: restoreExposure, grab: () => grabFrame(video, { ...options, waitMs: 0 }),
        });
        return { mode: 'bracket', shots: shots.map(s => ({ ev: s.ev, blob: s.shot })) };
      } catch (e) {
        if (!(e instanceof BracketRefused)) throw e;
        if (mounted.current) setRefused('bracket');
        return { mode: 'single', refused: 'bracket', shots: [{ blob: await grabFrame(video, { ...options, waitMs: 0 }) }] };
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [current, frames, applyBracket, restoreExposure]);

  return { mode: current, modes, setMode: choose, frames, setFrames, busy, refused, run };
}
